import { Row, buildActionColumn, buildAvatarDetailsColumn, buildBulkSelectionColumn } from "@/components/Table";
import { StudentRow } from "../types";
import { getStatusTagColor } from "../../_utils";
import { REQUIRED_INTERNSHIP_MONTHS } from "../../../_shared/constants";
import { buildPATStatus } from "../_utils/patStatus";
import {
  buildCohortColumn,
  buildEmailColumn,
  buildPATStatusColumn,
  buildInternshipMonthsColumn,
  buildProgramLevelColumn,
  buildUniversityStudentDetailLink
} from "../../../_shared/_utils";

// Helper function to determine graduation eligibility
export const isEligibleForGraduation = (student: StudentRow): boolean => {
  return !!(
    student.eligibleForGraduation === true &&
    student.playingAbilityTestStatus?.completed === true &&
    (student.totalInternshipMonths ?? 0) >= REQUIRED_INTERNSHIP_MONTHS
  );
};

export const mapStudents = (studentsData: any): StudentRow[] => {
  if (!studentsData || !Array.isArray(studentsData)) return [];

  return studentsData.map((node: any) => ({
    ...node,
    name: node.person?.displayName || `${node.person?.firstName} ${node.person?.lastName}`,
    program: {
      version: node.person?.educationLatestPgmProgram?.curriculumVersion,
      level: node.person?.educationLatestPgmProgram?.level
    },
    graduationDate: node.graduationDate
      ? new Date(node.graduationDate).toLocaleDateString("en-US", {
          month: "short",
          day: "2-digit",
          year: "numeric"
        })
      : "",
    cohortStatus: node.person?.cohortStatus || "In-Cohort",
    universityCohort: node.person?.universityCohort?.name || null,
    internshipMonths: node.person?.totalInternshipMonths ?? 0,
    patStatus: buildPATStatus(node.person?.playingAbilityTestStatus).value,
    profilePhoto: node.person?.profilePhoto,
    pgaId: node.person?.pgaId,
    gender: node.person?.gender,
    email: node.person?.email,
    programLevels: node.person?.educationPgmProgramHistory || [],
    completed: node.person?.educationLatestPgmProgram?.completed,
    id: node.id
  }));
};

export const mapGraduationRequestsFromUniversities = (universitiesData: any): StudentRow[] => {
  if (!universitiesData?.universities || !Array.isArray(universitiesData.universities)) return [];

  const allGraduationRequests: any[] = [];

  // Extract all graduation requests from all universities
  universitiesData.universities.forEach((university: any) => {
    if (university.graduationRequests && Array.isArray(university.graduationRequests)) {
      allGraduationRequests.push(...university.graduationRequests);
    }
  });

  // Map the graduation requests to StudentRow format
  return allGraduationRequests.map((graduationRequest: any) => ({
    ...graduationRequest,
    name:
      graduationRequest.person?.displayName ||
      `${graduationRequest.person?.firstName} ${graduationRequest.person?.lastName}`,
    program: {
      version: graduationRequest.person?.educationLatestPgmProgram?.curriculumVersion,
      level: graduationRequest.person?.educationLatestPgmProgram?.level
    },
    graduationDate: graduationRequest.graduationDate
      ? new Date(graduationRequest.graduationDate).toLocaleDateString("en-US", {
          month: "short",
          day: "2-digit",
          year: "numeric"
        })
      : "",
    cohortStatus: graduationRequest.person?.cohortStatus || "Not Found",
    universityCohort: graduationRequest.person?.universityCohort?.name || null,
    internshipMonths: graduationRequest.person?.totalInternshipMonths ?? 0,
    totalInternshipMonths: graduationRequest.person?.totalInternshipMonths ?? 0,
    patStatus: buildPATStatus(graduationRequest.person?.playingAbilityTestStatus).value,
    profilePhoto: graduationRequest.person?.profilePhoto,
    pgaId: graduationRequest.person?.pgaId,
    gender: graduationRequest.person?.gender,
    email: graduationRequest.person?.email,
    programLevels: graduationRequest.person?.educationPgmProgramHistory || [],
    completed: graduationRequest.person?.educationLatestPgmProgram?.completed,
    playingAbilityTestStatus: graduationRequest.person?.playingAbilityTestStatus,
    eligibleForGraduation: graduationRequest.person?.educationLatestPgmProgram?.eligibleForGraduation,
    id: graduationRequest.id,
    // Include graduation request specific fields
    graduationRequestId: graduationRequest.id,
    graduationRequestStatus: graduationRequest.status,
    submittedBy: graduationRequest.submittedBy,
    processedBy: graduationRequest.processedBy,
    processedAt: graduationRequest.processedAt,
    rejectionReason: graduationRequest.rejectionReason
  }));
};

type BuildColumnsProps = {
  isCompactLayout: boolean;
  hasCheckbox?: boolean;
  navigate: (to: string) => void;
  location: { pathname: string; search: string };
  searchParamsUrl: string;
  universityId: string;
  handleViewStudent: (student: StudentRow) => void;
  setMenu: (menu: any) => void;
  actionId: (rowId: string) => string;
  handleRemoveFromEligible: (graduationRequestId: string) => void;
};

export const buildColumns = ({
  isCompactLayout,
  hasCheckbox = false,
  navigate,
  location,
  searchParamsUrl,
  universityId,
  handleViewStudent,
  setMenu,
  actionId,
  handleRemoveFromEligible
}: BuildColumnsProps) => {
  return [
    ...(hasCheckbox ? [buildBulkSelectionColumn<StudentRow>()] : []),
    ...buildAvatarDetailsColumn<StudentRow>({
      isCompactLayout,
      getTitle: (row) => row.name,
      getSubtitle: (row) => `PGA ID: ${row.pgaId}`,
      getPicture: (row) => row.profilePhoto,
      header: "Name",
      size: 280,
      minSize: 220,
      sortType: "text"
    }),
    buildEmailColumn(),
    buildCohortColumn<StudentRow>({
      getStatusTagColor,
      getCohortValue: (row) => row.universityCohort?.name || null,
      displayMode: "text"
    }),
    buildProgramLevelColumn<StudentRow>({
      getProgram: (row) => row.program
    }),
    buildInternshipMonthsColumn<StudentRow>(),
    buildPATStatusColumn<StudentRow>({
      getStatusTagColor,
      getPATStatus: (row) => row.patStatus
    }),
    {
      accessorKey: "eligibility",
      header: "Graduation Eligible",
      enableSorting: false,
      cell: ({ row }: { row: Row<StudentRow> }) => {
        const eligible = isEligibleForGraduation(row.original);
        return (
          <div className="flex items-center gap-2">
            <div
              className={`w-3 h-3 rounded-full ${eligible ? "bg-extended-success-brand-color" : "bg-sys-brand-error"}`}
              title={eligible ? "Eligible for graduation" : "Not eligible for graduation"}
            />
            <span>{eligible ? "Eligible" : "Not Eligible"}</span>
          </div>
        );
      }
    },
    buildActionColumn<StudentRow>({
      getActions: (row) => [
        {
          text: "View Student",
          onClick: () => {
            const link = buildUniversityStudentDetailLink({
              universityId,
              pgaId: row.pgaId,
              returnPath: `${location.pathname}${location.search}`,
              searchParamsUrl
            });
            navigate(link.to);
          }
        },
        {
          text: "Remove from Eligible for Graduation",
          destructive: true,
          onClick: () => {
            handleRemoveFromEligible(row.id);
          }
        }
      ],
      onViewClick: (row) => handleViewStudent(row),
      viewButtonTitle: "View Student",
      actionId,
      setMenu
    })
  ];
};
